import { jsonCodec } from 'api-validations/zod-extra'
import { ApiError, path } from 'utils'
import { z } from 'zod'

import { OperationLevelPermission } from '../auth-claims/permissions'
import { boundarySchema, enrichedFieldBoundaryCollectionSchema, rxSchema } from '../geojson-schemas'
import { defineApi } from '../http'
import { SharingConnectionType } from '../partner-access/connection-types'
import { granularMixSchema, hybridSchema, liquidMixSchema } from '../products'
import { MeasureSystem, manyItems, oneItem } from '../shared'
import { controllableApplicationTypes } from '../stitch'
import { GeoSpatialFileType } from './geo-spatial-file-type'
import {
  guidancePlanSchema,
  guidancePlanSummarySchema,
  guidancePrimitiveSchema,
  lineSetSchema
} from './guidance'
import {
  baseCffSchema,
  type cffSchema,
  fieldAssetSchema,
  planSchema,
  productAssetSchema
} from './plan'

export * from './geo-spatial-file-type'
export * from './plan'
export * from './soil-sampling'

export const shapeFileSchema = z.object({
  dbf: z
    .instanceof(File)
    .refine((file) => file.name.toLowerCase().endsWith('.dbf'), {
      message: 'File must be a .dbf file'
    })
    .optional(),
  shp: z.instanceof(File).refine((file) => file.name.toLowerCase().endsWith('.shp'), {
    message: 'File must be a .shp file'
  }),
  shx: z.instanceof(File).refine((file) => file.name.toLowerCase().endsWith('.shx'), {
    message: 'File must be a .shx file'
  })
})

export const zipFileSchema = z.object({
  zip: z.instanceof(File).refine((file) => file.name.toLowerCase().endsWith('.zip'), {
    message: 'File must be a .zip file'
  })
})

export const gen3FileSchema = z.object({
  file: z.instanceof(File).refine((file) => file.name.endsWith('.2020'), {
    message: 'File must be a .2020 file'
  })
})

const locutusShapeFileSchema = z.object({
  ...shapeFileSchema.shape,
  type: z.literal(GeoSpatialFileType.ShapeFile)
})

const locutusZippedShapeFileSchema = z.object({
  ...zipFileSchema.shape,
  type: z.literal(GeoSpatialFileType.ShapeFile)
})

const locutusGen3FileSchema = z.object({
  ...gen3FileSchema.shape,
  type: z.literal(GeoSpatialFileType.Gen3_2020)
})

export const granularTankMixPlanAssetSchema = productAssetSchema(granularMixSchema)
export const liquidTankMixPlanAssetSchema = productAssetSchema(liquidMixSchema)
export const hybridPlanAssetSchema = productAssetSchema(hybridSchema)
export const rxPlanAssetSchema = fieldAssetSchema(rxSchema)
const { data: _boundaryData, ...boundaryAssetFields } = fieldAssetSchema(boundarySchema).shape
export const boundaryPlanAssetMetadataSchema = z.object({
  ...boundaryAssetFields,
  inverted: z.boolean().default(false)
})
export const boundaryPlanAssetSchema = z
  .object({ ...boundaryPlanAssetMetadataSchema.shape, data: boundarySchema })
  .refine((asset) => asset.inverted === asset.data.inverted, {
    message: 'Inverted flag must match between top-level and data field'
  })

export const guidancePrimitiveAssetSchema = fieldAssetSchema(guidancePrimitiveSchema).omit({
  crop: true, // guidance isn't crop-specific
  customCrop: true
})

export const guidancePlanAssetSchema = fieldAssetSchema(guidancePlanSchema).omit({
  crop: true, // guidance isn't crop-specific
  customCrop: true
})

/**
 * A guidance plan as listed: the full asset lives only in S3, because its
 * field features can be too large for a DynamoDB item.
 */
export const guidancePlanMetadataSchema = guidancePlanAssetSchema.extend({
  data: guidancePlanSummarySchema
})

export const lineSetAssetSchema = fieldAssetSchema(lineSetSchema).omit({
  crop: true, // guidance isn't crop-specific
  customCrop: true
})

/**
 * Prescription metadata — the full prescription asset without its (large) zone
 * geometry, plus the denormalized application type. Used wherever prescriptions
 * are listed rather than opened: the list endpoints and the PlanTable metadata row.
 */
export const prescriptionMetadataSchema = z.object({
  ...rxPlanAssetSchema.omit({ data: true }).shape,
  applicationType: controllableApplicationTypes,
  /**
   * Whether the stored asset is too large for the detail and download
   * endpoints to return; they answer 413 for it, so a client should not ask.
   * Decided when the asset is written. Absent on assets written before it
   * was recorded.
   */
  tooLargeToServe: z.boolean().optional()
})

/**
 * Why reading a stored plan asset failed. A 413 is the one expected failure —
 * the asset exists but exceeds what the endpoint can return — so the enum
 * separates it from everything else, whose free text is surfaced verbatim.
 */
export enum PlanAssetReadError {
  TooLarge = 'tooLarge',
  Other = 'other'
}

export type PlanAssetReadErrorResult =
  | { type: PlanAssetReadError.TooLarge }
  | { type: PlanAssetReadError.Other; message: string }

export const mapPlanAssetReadError = (error: unknown): PlanAssetReadErrorResult => {
  if (error instanceof ApiError && error.status === 413) {
    return { type: PlanAssetReadError.TooLarge }
  }
  return {
    type: PlanAssetReadError.Other,
    message: error instanceof Error ? error.message : 'Unknown error'
  }
}

export namespace Locutus {
  export type Field = z.infer<typeof cffSchema>
  export type Plan = z.infer<typeof planSchema>
  export type GranularTankMixDetails = z.infer<typeof granularMixSchema>
  export type LiquidTankMixDetails = z.infer<typeof liquidMixSchema>
  export type HybridInputDetails = z.infer<typeof hybridSchema>
  export type GranularTankMixPlanAsset = z.infer<typeof granularTankMixPlanAssetSchema>
  export type LiquidTankMixPlanAsset = z.infer<typeof liquidTankMixPlanAssetSchema>
  export type HybridPlanAsset = z.infer<typeof hybridPlanAssetSchema>
  export type RxPlanAsset = z.infer<typeof rxPlanAssetSchema>
  export type BoundaryPlanAsset = z.infer<typeof boundaryPlanAssetSchema>
  export type GuidancePrimitiveAsset = z.infer<typeof guidancePrimitiveAssetSchema>
  export type GuidancePlanAsset = z.infer<typeof guidancePlanAssetSchema>
  export type LineSetAsset = z.infer<typeof lineSetAssetSchema>
}

/**
 * Update a plan
 */
export const locutusModifyPlanApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string(),
    fieldId: z.string(),
    id: z.string()
  }),
  pathFor: (p) => `/locutus/${p.orgCode}/plans/${p.fieldId}/${p.id}`,
  name: 'UpdateFieldPlan',
  description: 'Update an existing planting or application plan for a specific field',
  requiredPermissions: OperationLevelPermission.ManagePlans,
  gatewayPath: '/:orgCode/plans/:fieldId/:id' as const,
  responseSchema: z.void(),
  requestBodySchema: planSchema
    .omit({
      id: true,
      orgCode: true,
      fieldId: true,
      lastModified: true,
      lastModifiedBy: true,
      createdAt: true,
      createdBy: true
    })
    .strict(),
  method: 'PUT',
  supportsCodec: true
})

/**
 * Delete a plan
 */
export const locutusDeletePlanApi = defineApi({
  ...locutusModifyPlanApi,
  name: 'DeleteFieldPlan',
  description: 'Delete a planting or application plan from a field',
  requestBodySchema: undefined,
  method: 'DELETE'
})

/**
 * Create a new plan for a field
 */
export const locutusCreatePlanApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string(),
    fieldId: z.string()
  }),
  pathFor: (p) => `/locutus/${p.orgCode}/plans/${p.fieldId}`,
  name: 'CreateFieldPlan',
  description: 'Create a new planting or application plan for a specific field',
  requiredPermissions: OperationLevelPermission.ManagePlans,
  method: 'POST',
  requestBodySchema: planSchema
    .omit({
      id: true,
      orgCode: true,
      fieldId: true,
      lastModified: true,
      lastModifiedBy: true,
      createdAt: true,
      createdBy: true
    })
    .strict(),
  gatewayPath: '/:orgCode/plans/:fieldId' as const,
  responseSchema: planSchema,
  supportsCodec: true
})

/**
 * Get all plans for an operation
 */
export const locutusGetAllPlansApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  pathFor: (p) => `/locutus/${p.orgCode}/plans`,
  name: 'GetOperationPlans',
  description: 'Retrieve all field plans for an operation, organized by field ID',
  requiredPermissions: OperationLevelPermission.ViewPlans,
  gatewayPath: '/:orgCode/plans' as const,
  responseSchema: z.record(z.string(), z.array(planSchema))
})

const sharedAttributes = {
  fieldId: z.string(),
  name: z.string()
}

const sharedPrescriptionAttributes = {
  ...sharedAttributes,
  applicationType: controllableApplicationTypes,
  measureSystem: z.enum(MeasureSystem)
}

/**
 * Boundary metadata with the default marker resolved per request from the
 * field's approximation, rather than stored on the asset where it could drift.
 */
export const listedBoundarySchema = boundaryPlanAssetMetadataSchema.extend({
  isDefault: z.boolean()
})

export const getAllBoundariesApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  querySchema: z.object({
    fieldId: z.string().optional()
  }),
  pathFor: ({ orgCode, fieldId }) => path(`/locutus/${orgCode}/boundaries`, { fieldId }),
  name: 'GetFieldBoundaries',
  description:
    'Retrieve metadata for all field boundaries in an operation (without geometry data), optionally filtered to a single field by id',
  requiredPermissions: OperationLevelPermission.ViewPlans,
  queryKey: ({ orgCode, fieldId }) => [orgCode, 'boundaries', { fieldId }],
  responseSchema: manyItems(listedBoundarySchema),
  gatewayPath: '/:orgCode/boundaries' as const
})

export const getAllBoundariesForPartnerApi = defineApi({
  ...getAllBoundariesApi,
  name: 'GetFieldBoundariesForPartner',
  description: 'Retrieve metadata for all field boundaries for external partner access',
  gatewayPath: '/export/planning/:orgCode/boundaries' as const,
  connectionTypes: [
    SharingConnectionType.AgronomicAdvisor,
    SharingConnectionType.ExportFieldPlanningData,
    SharingConnectionType.BidirectionalFieldPlanningData
  ]
})

export type AllBoundariesResponseType = z.infer<typeof getAllBoundariesApi.responseSchema>

/**
 * Which boundary asset a field's map polygon, acreage, and hit detection are
 * derived from. A field has at most one; a field whose only boundaries are
 * inverted has none, since an inverted boundary describes an excluded area
 * rather than the extent of the field.
 */
export const defaultBoundarySchema = z.object({
  fieldId: z.string(),
  boundaryId: z.string()
})

export const getDefaultBoundaryApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string(),
    fieldId: z.string()
  }),
  pathFor: ({ orgCode, fieldId }) => `/locutus/${orgCode}/fields/${fieldId}/default-boundary`,
  name: 'GetDefaultFieldBoundary',
  description:
    "Retrieve which boundary asset is currently a field's default — the one its map polygon, acreage, and hit detection are derived from. Returns 404 for a field with no boundary, or with only inverted ones.",
  requiredPermissions: OperationLevelPermission.ViewPlans,
  queryKey: ({ orgCode, fieldId }) => [orgCode, 'default-boundaries', fieldId],
  responseSchema: defaultBoundarySchema,
  gatewayPath: '/:orgCode/fields/:fieldId/default-boundary' as const
})

export const setDefaultBoundaryApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string(),
    fieldId: z.string()
  }),
  pathFor: ({ orgCode, fieldId }) => `/locutus/${orgCode}/fields/${fieldId}/default-boundary`,
  name: 'SetDefaultFieldBoundary',
  method: 'PUT',
  description:
    "Choose which of a field's boundary assets is its default, regenerating the field's simplified map polygon, acreage, and hit-detection geometry from it. Rejects a boundary that belongs to a different field, has been deleted, or is inverted.",
  requiredPermissions: OperationLevelPermission.ManagePlans,
  requestBodySchema: defaultBoundarySchema.pick({ boundaryId: true }),
  responseSchema: z.void(),
  gatewayPath: '/:orgCode/fields/:fieldId/default-boundary' as const,
  invalidateQueries: ({ orgCode }) => [
    [orgCode, 'default-boundaries'],
    [orgCode, 'boundaries'],
    [orgCode, 'field-plans'],
    [orgCode, 'fields', 'polygons']
  ]
})

export const getBoundaryApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string(),
    boundaryId: z.string()
  }),
  pathFor: (p) => `/locutus/${p.orgCode}/boundaries/${p.boundaryId}`,
  name: 'GetBoundaryDetails',
  description: 'Retrieve complete boundary data including geometry for a specific field',
  requiredPermissions: OperationLevelPermission.ViewPlans,
  responseSchema: boundaryPlanAssetSchema,
  gatewayPath: '/:orgCode/boundaries/:boundaryId' as const,
  ttlSeconds: Number.POSITIVE_INFINITY,
  errorMap: mapPlanAssetReadError
})

export const getBoundaryForPartnerApi = defineApi({
  ...getBoundaryApi,
  name: 'GetBoundaryDetailsForPartner',
  description: 'Retrieve complete boundary data for external partner access',
  gatewayPath: '/export/planning/:orgCode/boundaries/:boundaryId' as const,
  connectionTypes: [
    SharingConnectionType.AgronomicAdvisor,
    SharingConnectionType.ExportFieldPlanningData,
    SharingConnectionType.BidirectionalFieldPlanningData
  ]
})

export const getPrescriptionApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string(),
    prescriptionId: z.string()
  }),
  pathFor: (p) => `/locutus/${p.orgCode}/prescriptions/${p.prescriptionId}`,
  name: 'GetPrescriptionDetails',
  description:
    'Retrieve complete prescription data including rates and zones for a specific prescription',
  requiredPermissions: OperationLevelPermission.ViewPlans,
  responseSchema: rxPlanAssetSchema,
  gatewayPath: '/:orgCode/prescriptions/:prescriptionId' as const,
  errorMap: mapPlanAssetReadError
})

export const getAllPrescriptionsApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  querySchema: z.object({
    fieldId: z.string().optional()
  }),
  pathFor: ({ orgCode, fieldId }) => path(`/locutus/${orgCode}/prescriptions`, { fieldId }),
  name: 'GetAllPrescriptions',
  description:
    'Retrieve metadata for all prescriptions in an operation (without zone data), optionally filtered to a single field by id',
  requiredPermissions: OperationLevelPermission.ViewPlans,
  gatewayPath: '/:orgCode/prescriptions' as const,
  queryKey: ({ orgCode, fieldId }) => [orgCode, 'prescriptions', { fieldId }],
  responseSchema: manyItems(prescriptionMetadataSchema)
})

export const getAllPrescriptionsForPartnerApi = defineApi({
  ...getAllPrescriptionsApi,
  name: 'GetAllPrescriptionsForPartner',
  description: 'Retrieve metadata for all prescriptions for external partner access',
  gatewayPath: '/export/planning/:orgCode/prescriptions' as const,
  connectionTypes: [
    SharingConnectionType.ExportFieldPlanningData,
    SharingConnectionType.BidirectionalFieldPlanningData
  ]
})

export const getPrescriptionDetailsForPartnerApi = defineApi({
  ...getPrescriptionApi,
  name: 'GetPrescriptionDetailsForPartner',
  description: 'Retrieve complete prescription data for external partner access',
  gatewayPath: '/export/planning/:orgCode/prescriptions/:prescriptionId' as const,
  connectionTypes: [
    SharingConnectionType.ExportFieldPlanningData,
    SharingConnectionType.BidirectionalFieldPlanningData
  ]
})

export type AllPrescriptionsResponseType = z.infer<typeof getAllPrescriptionsApi.responseSchema>
export type ListGuidancePrimitivesResponse = z.infer<typeof listGuidancePrimitiveApi.responseSchema>

const planAssetRefSchema = z.object({ id: z.uuid(), name: z.string() })

/**
 * The field's assets that no named Plan references — the map sidebar "Unassigned"
 * group. Kept as a sibling of `plans`, not folded in as a null-id pseudo-plan.
 * Metadata only; geometry is fetched per-asset on selection via GetBoundaryDetails
 * and GetPrescriptionDetails.
 */
const unassignedFieldAssetsSchema = z.object({
  boundaries: z.array(boundaryPlanAssetMetadataSchema),
  prescriptions: z.array(prescriptionMetadataSchema),
  // Refs only — the picker shows a single Guidance row, so the client derives
  // presence from length rather than listing primitives individually.
  guidance: z.array(planAssetRefSchema)
})

const fieldPlansResponseSchema = z.object({
  fieldId: z.uuid(),
  plans: z.array(planSchema),
  unassigned: unassignedFieldAssetsSchema,
  /** Absent when the field has no boundary, or only inverted ones. */
  defaultBoundaryId: z.string().optional()
})

export type FieldPlansResponse = z.infer<typeof fieldPlansResponseSchema>

/**
 * Retrieve a single field's planning picture by its CFF name (client/farm/field):
 * its named plans with referenced assets, the unassigned
 * boundaries/prescriptions/guidance, and the resolved field id. Returns 404 if no
 * field with that CFF is registered for planning. Prefer this whenever a field is
 * known by name rather than by internal id.
 */
export const getFieldPlansApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  // CFF tuple is a required natural-key selector. It lives in querySchema, not the
  // path, because field names are free-text; requiring all three keeps the result
  // singular (the byCff GSI guarantees the tuple is unique).
  querySchema: baseCffSchema,
  pathFor: ({ orgCode, client, farm, field }) =>
    path(`/locutus/${orgCode}/field-plans`, { client, farm, field }),
  name: 'GetFieldPlans',
  description:
    "Retrieve a single field's planning picture by its CFF name (client/farm/field): its named plans with referenced assets, the unassigned boundaries/prescriptions/guidance, and the resolved field id. Returns 404 if the field is not registered for planning.",
  requiredPermissions: OperationLevelPermission.ViewPlans,
  queryKey: ({ orgCode, client, farm, field }) => [orgCode, 'field-plans', { client, farm, field }],
  responseSchema: fieldPlansResponseSchema,
  gatewayPath: '/:orgCode/field-plans' as const
})

/**
 * Retrieve a single field's planning picture by its planning field id — the same
 * shape as GetFieldPlans. For internal callers that already hold the field id
 * (e.g. a deep-linked plan asset on the map). The shared `field-plans` query-key
 * prefix means plan/asset mutations invalidate this and the CFF-keyed variant alike.
 *
 * Unlike GetFieldPlans (which 404s for an unregistered CFF), this returns an empty
 * `{ plans: [], unassigned: {…} }` for an unknown field id rather than 404 — it
 * does not verify the field exists. Callers are expected to pass ids resolved via
 * GetFieldPlans or held in map state, so a fabricated id yields an empty result.
 */
export const getFieldPlansByIdApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string(),
    fieldId: z.string()
  }),
  pathFor: ({ orgCode, fieldId }) => `/locutus/${orgCode}/fields/${fieldId}/plans`,
  name: 'GetFieldPlansById',
  description:
    "Retrieve a single field's planning picture by its planning field id — same shape as GetFieldPlans, for internal callers that already hold the id.",
  requiredPermissions: OperationLevelPermission.ViewPlans,
  queryKey: ({ orgCode, fieldId }) => [orgCode, 'field-plans', { fieldId }],
  responseSchema: fieldPlansResponseSchema,
  gatewayPath: '/:orgCode/fields/:fieldId/plans' as const
})

export const deleteBoundaryApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string(),
    boundaryId: z.string()
  }),
  pathFor: (p) => `/locutus/${p.orgCode}/boundaries/${p.boundaryId}`,
  name: 'DeleteBoundary',
  description: 'Delete a field boundary from the planning system',
  requiredPermissions: OperationLevelPermission.ManagePlans,
  gatewayPath: '/:orgCode/boundaries/:boundaryId' as const,
  method: 'DELETE',
  responseSchema: z.void(),
  // Prefix matches: the org's boundary list (and per-field variants) plus the
  // field-plans picker, which surfaces unassigned boundaries.
  invalidateQueries: ({ orgCode }) => [
    [orgCode, 'boundaries'],
    [orgCode, 'field-plans'],
    [orgCode, 'default-boundaries'],
    [orgCode, 'fields', 'polygons']
  ]
})

export const deletePrescriptionApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string(),
    prescriptionId: z.string()
  }),
  pathFor: (p) => `/locutus/${p.orgCode}/prescriptions/${p.prescriptionId}`,
  name: 'DeletePrescription',
  description: 'Delete a prescription from the planning system',
  requiredPermissions: OperationLevelPermission.ManagePlans,
  method: 'DELETE',
  gatewayPath: '/:orgCode/prescriptions/:prescriptionId' as const,
  responseSchema: z.void(),
  // Prefix matches: the org's prescription list (and per-field variants) plus the
  // field-plans picker, which surfaces unassigned prescriptions.
  invalidateQueries: ({ orgCode }) => [
    [orgCode, 'prescriptions'],
    [orgCode, 'field-plans']
  ]
})

/** Why a plan asset create failed. An empty `message` indicates there is no reason worth displaying. */
export enum PlanAssetCreateError {
  DuplicateName = 'duplicateName',
  /**
   * The request body is larger than API Gateway will pass to locutus, so it refused the
   * request before locutus could see it.
   */
  TooLarge = 'tooLarge',
  Other = 'other'
}

export type PlanAssetCreateErrorResult =
  | { type: PlanAssetCreateError.DuplicateName }
  | { type: PlanAssetCreateError.TooLarge }
  | { type: PlanAssetCreateError.Other; message: string }

/**
 * Crusher's body when it refuses an upload, which locutus forwards as a 400.
 */
export const crusherRefusalSchema = jsonCodec(z.object({ message: z.string().optional() }))

export const mapPlanAssetCreateError = (error: unknown): PlanAssetCreateErrorResult => {
  if (!(error instanceof ApiError)) {
    return {
      type: PlanAssetCreateError.Other,
      message: error instanceof Error ? error.message : ''
    }
  }

  if (error.status === 409) return { type: PlanAssetCreateError.DuplicateName }
  if (error.status === 413) return { type: PlanAssetCreateError.TooLarge }
  if (error.status !== 400) return { type: PlanAssetCreateError.Other, message: '' }

  const refusal = crusherRefusalSchema.safeParse(error.responseText)
  return {
    type: PlanAssetCreateError.Other,
    message: refusal.success ? (refusal.data.message ?? '') : error.responseText
  }
}

/**
 * Create a new boundary
 */
export const locutusCreateBoundaryApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  pathFor: (p) => `/locutus/${p.orgCode}/assets/boundary`,
  name: 'CreateFieldBoundary',
  description: 'Create a new field boundary from uploaded file (shapefile, 2020, or GeoJSON)',
  requiredPermissions: OperationLevelPermission.ManagePlans,
  method: 'POST',
  requestBodySchema: z.union([
    locutusShapeFileSchema.extend(sharedAttributes),
    locutusZippedShapeFileSchema.extend(sharedAttributes),
    locutusGen3FileSchema.extend(sharedAttributes),
    z
      .object({
        type: z.literal(GeoSpatialFileType.GeoJSON),
        boundary: jsonCodec(boundarySchema.shape.boundary),
        inverted: z.union([z.boolean(), z.stringbool()]).default(false)
      })
      .extend(sharedAttributes)
  ]),
  requestBodyEncoding: 'form-data',
  responseSchema: oneItem(boundaryPlanAssetMetadataSchema.pick({ id: true, name: true })),
  gatewayPath: '/:orgCode/assets/boundary' as const,
  // An upload claims the field's default, which the map polygons derive from.
  invalidateQueries: ({ orgCode }) => [
    [orgCode, 'boundaries'],
    [orgCode, 'field-plans'],
    [orgCode, 'fields', 'polygons']
  ],
  supportsCodec: true,
  errorMap: mapPlanAssetCreateError
})

export const locutusCreateBoundaryForPartnerApi = defineApi({
  ...locutusCreateBoundaryApi,
  name: 'CreateFieldBoundaryForPartner',
  description: 'Create a new field boundary via partner import',
  gatewayPath: '/import/planning/:orgCode/assets/boundary' as const,
  connectionTypes: [
    SharingConnectionType.ImportFieldPlanningData,
    SharingConnectionType.BidirectionalFieldPlanningData,
    SharingConnectionType.AgronomicAdvisor
  ]
})

/**
 * Create a new prescription
 */
export const locutusCreateRxApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  pathFor: (p) => path(`/locutus/${p.orgCode}/assets/prescription`),
  name: 'CreatePrescription',
  description: 'Create a new prescription from uploaded file (shapefile, 2020, or GeoJSON)',
  requiredPermissions: OperationLevelPermission.ManagePlans,
  method: 'POST',
  requestBodySchema: z.union([
    locutusShapeFileSchema.required().extend(sharedPrescriptionAttributes),
    locutusZippedShapeFileSchema.extend(sharedPrescriptionAttributes),
    locutusGen3FileSchema.extend(sharedPrescriptionAttributes),
    z
      .object({
        type: z.literal(GeoSpatialFileType.GeoJSON),
        prescription: jsonCodec(rxSchema.shape.prescription)
      })
      .extend(sharedPrescriptionAttributes)
  ]),
  requestBodyEncoding: 'form-data',
  responseSchema: oneItem(
    rxPlanAssetSchema.pick({ id: true, name: true }).and(rxSchema.pick({ type: true }))
  ),
  gatewayPath: '/:orgCode/assets/prescription' as const,
  invalidateQueries: ({ orgCode }) => [
    [orgCode, 'prescriptions'],
    [orgCode, 'field-plans']
  ],
  supportsCodec: true,
  errorMap: mapPlanAssetCreateError
})

export const locutusCreateRxForPartnerApi = defineApi({
  ...locutusCreateRxApi,
  name: 'CreatePrescriptionForPartner',
  description: 'Create a new prescription via partner import',
  gatewayPath: '/import/planning/:orgCode/assets/prescription' as const,
  connectionTypes: [
    SharingConnectionType.ImportFieldPlanningData,
    SharingConnectionType.BidirectionalFieldPlanningData,
    SharingConnectionType.AgronomicAdvisor
  ]
})

export const locutusDownloadFileAssetApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string(),
    fileId: z.string(),
    type: z.enum(['rx', 'boundary'])
  }),
  pathFor: (p) => path(`/locutus/${p.orgCode}/download/${p.type}/${p.fileId}`, { as: p.as }),
  name: 'DownloadPlanningAsset',
  description: 'Download a boundary or prescription file in shapefile or 2020 format',
  requiredPermissions: OperationLevelPermission.ViewPlans,
  headersSchema: z.object({
    authorization: z.string()
  }),
  querySchema: z.object({
    as: z
      .enum([GeoSpatialFileType.Gen3_2020, GeoSpatialFileType.ShapeFile])
      .default(GeoSpatialFileType.Gen3_2020)
  }),
  gatewayPath: '/:orgCode/download/:type/:fileId' as const,
  responseSchema: z.array(z.object({ url: z.string() })),
  errorMap: mapPlanAssetReadError
})

export const getAllFieldPolygonsApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  pathFor: (p) => `/locutus/${p.orgCode}/fields/polygons`,
  name: 'GetFieldPolygons',
  description: 'Retrieve simplified polygon approximations for all fields in an operation',
  requiredPermissions: OperationLevelPermission.ViewPlans,
  queryKey: (p) => [p.orgCode, 'fields', 'polygons'],
  responseSchema: enrichedFieldBoundaryCollectionSchema,
  gatewayPath: '/:orgCode/fields/polygons' as const,
  ttlSeconds: Number.POSITIVE_INFINITY
})

export const postGuidancePrimitiveApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  pathFor: ({ orgCode }) => `/locutus/${orgCode}/guidance/primitive`,
  name: 'PostGuidancePrimitive',
  method: 'POST',
  description: 'Create a new guidance primitive that can be referenced in plans',
  requiredPermissions: OperationLevelPermission.ManagePlans,
  responseSchema: z.object({ id: z.uuid() }),
  gatewayPath: '/:orgCode/guidance/primitive' as const,
  requestBodySchema: guidancePrimitiveAssetSchema.pick({
    name: true,
    fieldId: true,
    data: true,
    type: true
  }),
  invalidateQueries: ({ orgCode }) => [orgCode, 'field-plans']
})

export const deleteGuidancePrimitiveApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string(),
    id: z.string()
  }),
  pathFor: ({ orgCode, id }) => `/locutus/${orgCode}/guidance/primitive/${id}`,
  name: 'DeleteGuidancePrimitive',
  method: 'DELETE',
  description: 'Delete a guidance primitive from the system',
  requiredPermissions: OperationLevelPermission.ManagePlans,
  gatewayPath: '/:orgCode/guidance/primitive/:id' as const,
  responseSchema: z.void(),
  invalidateQueries: ({ orgCode }) => [
    [orgCode, 'field-plans'],
    [orgCode, 'primitives']
  ]
})

export const listGuidancePrimitiveApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  querySchema: z.object({
    fieldId: z.string().optional()
  }),
  queryKey: (p) => [p.orgCode, 'primitives', p.fieldId],
  pathFor: ({ orgCode, fieldId }) => path(`/locutus/${orgCode}/guidance/primitives`, { fieldId }),
  name: 'ListGuidancePrimitives',
  method: 'GET',
  description: 'List all guidance primitives for an operation, with optional filtering by field',
  requiredPermissions: OperationLevelPermission.ViewPlans,
  gatewayPath: '/:orgCode/guidance/primitives' as const,
  responseSchema: manyItems(guidancePrimitiveAssetSchema)
})

export const postGuidancePlanApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  pathFor: ({ orgCode }) => `/locutus/${orgCode}/guidance/plan`,
  name: 'PostGuidancePlan',
  method: 'POST',
  description:
    'Create a guidance plan, which assigns a line to each region of a field, that any plan on the field can reference',
  requiredPermissions: OperationLevelPermission.ManagePlans,
  responseSchema: guidancePlanAssetSchema.pick({ id: true, name: true }),
  gatewayPath: '/:orgCode/guidance/plan' as const,
  requestBodySchema: guidancePlanAssetSchema.pick({
    name: true,
    fieldId: true,
    data: true
  }),
  invalidateQueries: ({ orgCode }) => [orgCode, 'guidance-plans']
})

export const getGuidancePlanApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string(),
    id: z.string()
  }),
  pathFor: ({ orgCode, id }) => `/locutus/${orgCode}/guidance/plan/${id}`,
  name: 'GetGuidancePlan',
  description:
    'Retrieve a guidance plan in full, including its field features (regions, edges, and corners)',
  requiredPermissions: OperationLevelPermission.ViewPlans,
  queryKey: ({ orgCode, id }) => [orgCode, 'guidance-plans', id],
  responseSchema: guidancePlanAssetSchema,
  gatewayPath: '/:orgCode/guidance/plan/:id' as const,
  errorMap: mapPlanAssetReadError
})

export const listGuidancePlansApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  querySchema: z.object({
    fieldId: z.string().optional()
  }),
  queryKey: ({ orgCode, fieldId }) => [orgCode, 'guidance-plans', { fieldId }],
  pathFor: ({ orgCode, fieldId }) => path(`/locutus/${orgCode}/guidance/plans`, { fieldId }),
  name: 'ListGuidancePlans',
  method: 'GET',
  description:
    "List an operation's guidance plans, optionally for one field, without their field features",
  requiredPermissions: OperationLevelPermission.ViewPlans,
  gatewayPath: '/:orgCode/guidance/plans' as const,
  responseSchema: manyItems(guidancePlanMetadataSchema)
})

export const postLineSetApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  pathFor: ({ orgCode }) => `/locutus/${orgCode}/guidance/line-set`,
  name: 'PostLineSet',
  method: 'POST',
  description:
    'Create a line set, an ordered list of guidance lines that any plan on the field can reference',
  requiredPermissions: OperationLevelPermission.ManagePlans,
  responseSchema: lineSetAssetSchema.pick({ id: true, name: true }),
  gatewayPath: '/:orgCode/guidance/line-set' as const,
  requestBodySchema: lineSetAssetSchema.pick({
    name: true,
    fieldId: true,
    data: true
  }),
  invalidateQueries: ({ orgCode }) => [orgCode, 'line-sets']
})

export const listLineSetsApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  querySchema: z.object({
    fieldId: z.string().optional()
  }),
  queryKey: ({ orgCode, fieldId }) => [orgCode, 'line-sets', { fieldId }],
  pathFor: ({ orgCode, fieldId }) => path(`/locutus/${orgCode}/guidance/line-sets`, { fieldId }),
  name: 'ListLineSets',
  method: 'GET',
  description: "List an operation's line sets, optionally for one field",
  requiredPermissions: OperationLevelPermission.ViewPlans,
  gatewayPath: '/:orgCode/guidance/line-sets' as const,
  responseSchema: manyItems(lineSetAssetSchema)
})
